import EyeOff from '@/assets/icons/eye-off.svg';
import EyeOn from '@/assets/icons/eye-on.svg';
import AuthLayout from '@/components/AuthLayout';
import AppFormField from '@/components/common/AppFormField';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { createUser } from '../api/users';
import AppButton from '../components/common/AppButton';
import { useAuth } from '../context/AuthContext';

const registerSchema = yup.object({
  name: yup
    .string()
    .required('Your name is required')
    .min(2, 'Name must be at least 2 characters')
    .trim(),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .trim(),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password should have at least 8 characters')
    .max(18, 'Password can have at most 18 characters')
    .matches(/[A-Z]/, 'Password must contain at least 1 upper case letter')
    .matches(/[a-z]/, 'Password must contain at least 1 lower case letter')
    .matches(
      /[!@#$%^&*()_\-+=<>?]/,
      'Password must contain at least 1 special character'
    )
    .matches(/\d/, 'Password must contain at least 1 number'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  agreeToTerms: yup
    .boolean()
    .required('You must agree to the terms of service and privacy policy')
    .oneOf([true], 'You must agree to the terms of service and privacy policy'),
});

type RegisterFormValues = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
};

type FunctionsError = { code?: string; message?: string };

const getAuthError = (err: FunctionsError) => {
  switch (err.code) {
    case 'functions/already-exists':
      return 'Email already exists. Please log in instead.';
    case 'functions/invalid-argument':
      return err.message ?? 'Invalid request. Please check your details.';
    default:
      return 'Unexpected error occurred. Please try again.';
  }
};

const RegisterPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const methods = useForm<RegisterFormValues>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
    resolver: yupResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormValues) => {
    setLoading(true);
    setSubmitError('');

    const registerPromise = createUser({
      email: data.email,
      password: data.password,
      displayName: data.name,
    }).then(() => login(data.email, data.password));

    try {
      await toast.promise(registerPromise, {
        loading: 'Creating your account...',
        success: 'Account created successfully!',
        error: (err: FunctionsError) => getAuthError(err),
      });

      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      setSubmitError(getAuthError(error as FunctionsError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Register an account"
      subtitle={
        <div className="text-center">
          <p>
            Pool Guy is a business tool for pool service companies to manage
            their pools and client network.
          </p>
          <a
            href="https://getpoolguy.com"
            className="text-primary hover:text-secondary underline"
            target="_blank"
            rel="noreferrer"
          >
            Learn more on our website.
          </a>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col md:gap-8 gap-6"
        >
          <div className="flex flex-col gap-4">
            {submitError && (
              <div className="bg-dangerLight border border-danger/20 text-danger text-sm p-3 rounded-lg">
                {submitError}
              </div>
            )}

            <AppFormField<RegisterFormValues>
              name="name"
              label="Your Name"
              type="text"
              placeholder="John Doe"
            />

            <AppFormField<RegisterFormValues>
              name="email"
              label="Email Address"
              type="email"
              placeholder="john@example.com"
            />

            <AppFormField<RegisterFormValues>
              name="password"
              label="Create Password"
              type={showPassword ? 'text' : 'password'}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-light hover:text-dark"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff /> : <EyeOn />}
                </button>
              }
              placeholder="Create a secure password"
            />

            <AppFormField<RegisterFormValues>
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-light hover:text-dark"
                  aria-label={
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  }
                >
                  {showConfirmPassword ? <EyeOff /> : <EyeOn />}
                </button>
              }
              placeholder="Confirm your password"
            />

            <AppFormField<RegisterFormValues>
              name="agreeToTerms"
              fieldType="checkbox"
              id="agreeToTerms"
              label={
                <>
                  I have read and agree to the{' '}
                  <a
                    href="https://getpoolguy.com/terms"
                    className="text-primary hover:text-secondary underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://getpoolguy.com/privacy"
                    className="text-primary hover:text-secondary underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              }
            />
          </div>

          <div className="px-8">
            <AppButton type="submit" fullWidth loading={loading}>
              Register
            </AppButton>
          </div>
          <div className="flex items-center justify-center gap-2">
            <p className="text-light">Already have an account?</p>
            <a
              href="/login"
              className="text-primary hover:text-secondary font-semibold"
            >
              LOG IN
            </a>
          </div>
        </form>
      </FormProvider>
    </AuthLayout>
  );
};

export default RegisterPage;
